import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import AuthImg from "@/components/AuthImg";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";import { Star, ArrowLeft, Plus, Trash2, X, RefreshCw, ArrowUpDown, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Maximize2, Minimize2, ExternalLink, Gauge, Building2, StickyNote, ZoomIn, ZoomOut, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import ScreenshotUploader from "@/components/ScreenshotUploader";
import InsiderSummary from "@/components/InsiderEvents";
import BusinessQualityChecklist from "@/components/BusinessQualityChecklist";
import MarketHistory from "@/components/MarketHistory";
import { computeEstimatedPnl } from "@/lib/tradeMath";
import { useAuth } from "@/context/AuthContext";
import { canEditTrade, canDeleteTrade, canAddPhase, canOverrideDates, canEditExistingDates } from "@/lib/permissions";
import DateEditor from "@/components/DateEditor";
import TradeReactions from "@/components/TradeReactions";
import CloseTradeDialog from "@/components/CloseTradeDialog";
import { BUY_PHASES, SELL_PHASES, POSITION_PHASES, positionFromPhases, phasesToFills, phasePositionPct } from "@/lib/positionMath";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";

function fmtMoney(n) {
  if (n == null) return "—";
  const sign = n >= 0 ? "+" : "-";
  return `${sign}$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function TradeDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user: me } = useAuth();
  const [trade, setTrade] = useState(null);
  const [settings, setSettings] = useState(null);
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState({});
  const [newPhase, setNewPhase] = useState({ phase: "", notes: "", timeframe: "1D", criteria: [], screenshots: [], price: "", shares: "", confidence: null, created_at_override: "" });
  const [phaseOpen, setPhaseOpen] = useState(false);
  const [quote, setQuote] = useState(null);
  const [fundamentals, setFundamentals] = useState(null);
  const [lightbox, setLightbox] = useState(null); // number | null - index into allScreenshots
  const [phaseSort, setPhaseSort] = useState("desc"); // desc = newest first
  const [metricsPref, setMetricsPref] = useState(null); // {mode, custom_account_size, custom_size_pct}
  const [customDialogOpen, setCustomDialogOpen] = useState(false);
  const [customDraft, setCustomDraft] = useState({ account: "", pct: "" });
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  // Collapsible right-rail sections. Default expanded so users see everything
  // on first load; state is per-mount (deliberately not persisted so opening
  // a fresh trade always shows the full picture).
  const [setupCollapsed, setSetupCollapsed] = useState(false);
  const [fundCollapsed, setFundCollapsed] = useState(false);

  const load = async () => {
    const [t, s, mp] = await Promise.all([
      api.get(`/trades/${id}`),
      api.get("/settings"),
      api.get(`/trades/${id}/metrics-pref`).catch(() => ({ data: null })),
    ]);
    setTrade(t.data);
    setSettings(s.data);
    setMetricsPref(mp?.data || null);
    setEdit({
      entry_price: t.data.entry_price ?? "",
      entry_range_low: t.data.entry_range_low ?? "",
      entry_range_high: t.data.entry_range_high ?? "",
      exit_price: t.data.exit_price ?? "",
      stop_loss: t.data.stop_loss ?? "",
      take_profit_levels: (t.data.take_profit_levels && t.data.take_profit_levels.length > 0)
        ? t.data.take_profit_levels.map((lv) => ({ price: lv.price ?? "", size_pct: lv.size_pct ?? "" }))
        : (t.data.take_profit != null ? [{ price: t.data.take_profit, size_pct: "" }] : [{ price: "", size_pct: "" }]),
      logarithmic: !!t.data.logarithmic,
      status: t.data.status,
      notes: t.data.notes || "",
    });
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const refreshPrice = async ({ silent = false } = {}) => {
    if (!trade?.ticker) return;
    try {
      const { data } = await api.get(`/market/quote/${trade.ticker}`);
      setQuote(data);
    } catch { if (!silent) toast.error("Could not fetch quote"); }
  };

  // Auto-fetch the live quote as soon as the trade (and its ticker) is available.
  useEffect(() => {
    if (!trade?.ticker) return;
    refreshPrice({ silent: true });
    // Fundamentals (market cap / PE / float) fetched once per ticker; cached
    // server-side. Yahoo throttles occasionally return all-null payloads
    // (backend intentionally skips caching those), so retry with backoff up
    // to a few times before giving up — otherwise the card looks empty on
    // Prod even though the next reload would succeed.
    let cancelled = false;
    (async () => {
      const delays = [0, 800, 2000, 5000];
      for (let i = 0; i < delays.length; i++) {
        if (cancelled) return;
        if (delays[i]) await new Promise((r) => setTimeout(r, delays[i]));
        try {
          const { data } = await api.get(`/market/fundamentals/${trade.ticker}`);
          if (cancelled) return;
          setFundamentals(data);
          const hasAny = data && (data.market_cap != null || data.pe_ratio != null
            || data.float_shares != null || data.short_float_pct != null
            || data.beta != null || data.next_earnings_at != null);
          if (hasAny) return;
        } catch { /* silent — try again */ }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [trade?.ticker]);

  const saveEdits = async () => {
    const patch = { ...edit };
    ["entry_price","entry_range_low","entry_range_high","exit_price","stop_loss"].forEach((k) => {
      patch[k] = patch[k] === "" || patch[k] == null ? null : parseFloat(patch[k]);
    });
    patch.take_profit_levels = (edit.take_profit_levels || [])
      .filter((lv) => lv.price !== "" && lv.price != null)
      .map((lv) => ({
        price: parseFloat(lv.price),
        size_pct: lv.size_pct === "" || lv.size_pct == null ? null : parseFloat(lv.size_pct),
      }));
    patch.take_profit = patch.take_profit_levels[0]?.price ?? null;
    patch.logarithmic = !!edit.logarithmic;
    try {
      await api.put(`/trades/${id}`, patch);
      await load();
      setEditing(false);
      toast.success("Saved");
      window.dispatchEvent(new CustomEvent("trades-changed"));
    } catch { toast.error("Save failed"); }
  };

  const removeTrade = async () => {
    if (!confirm(`Delete trade ${trade.ticker}?`)) return;
    await api.delete(`/trades/${id}`);
    toast.success("Deleted");
    window.dispatchEvent(new CustomEvent("trades-changed"));
    nav("/journal");
  };

  const addPhase = async () => {
    if (!newPhase.phase) { toast.error("Choose a phase"); return; }
    // Position-affecting phases require a shares count.
    if (POSITION_PHASES.has(newPhase.phase)) {
      // Closed = full remaining position via FIFO. Prevent creating one when
      // nothing is on hand — force Open - Entry / Added first, or use
      // Closed - Update for a post-close note.
      if (newPhase.phase === "Closed") {
        if (Math.abs(currentPosition.position) < 1e-9) {
          toast.error("Nothing left to close. Add Open - Entry first, or use Closed - Update for a note.");
          return;
        }
        // Snap shares back to the full remaining position so a stale value
        // from a prior selection can't leak through.
        newPhase.shares = String(Math.abs(currentPosition.position));
        if (newPhase.price === "" || !parseFloat(newPhase.price)) {
          toast.error("Enter the closing price");
          return;
        }
      } else {
        const sh = parseFloat(newPhase.shares);
        if (!sh || sh <= 0) {
          toast.error(BUY_PHASES.has(newPhase.phase) ? "Enter the number of shares bought" : "Enter the number of shares sold");
          return;
        }
        if (SELL_PHASES.has(newPhase.phase) && sh > Math.abs(currentPosition.position) + 1e-9) {
          toast.error(`Cannot trim ${sh} sh — only ${Math.abs(currentPosition.position)} sh on hand`);
          return;
        }
      }
    }
    try {
      const payload = {
        ...newPhase,
        price: newPhase.price === "" ? null : parseFloat(newPhase.price),
        shares: newPhase.shares === "" ? null : parseFloat(newPhase.shares),
      };
      if (payload.created_at_override) {
        // datetime-local (no zone) → ISO
        payload.created_at_override = new Date(payload.created_at_override).toISOString();
      } else {
        delete payload.created_at_override;
      }
      await api.post(`/trades/${id}/phases`, payload);
      setNewPhase({ phase: "", notes: "", timeframe: "1D", criteria: [], screenshots: [], price: "", shares: "", confidence: null, created_at_override: "" });
      setPhaseOpen(false);
      await load();
      toast.success("Phase added");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const removePhase = async (phaseId) => {
    await api.delete(`/trades/${id}/phases/${phaseId}`);
    await load();
  };

  if (!trade) return <div className="p-8 text-zinc-500 text-sm">Loading…</div>;

  const phases = trade.phases || [];
  const currentPosition = positionFromPhases(phases, trade.direction);
  const sortedPhases = [...phases].sort((a, b) => {
    const av = a.created_at || "";
    const bv = b.created_at || "";
    return phaseSort === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
  });

  // Flat list of all screenshots across all phases for lightbox navigation.
  // Always chronological (oldest → newest) so the thumbnail strip reads
  // left-to-right in time order regardless of the timeline's sort direction.
  const chronoPhases = [...phases].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
  const allScreenshots = chronoPhases.flatMap((p) => (p.screenshots || []).map((s) => ({ path: s, phase: p.phase, created_at: p.created_at, notes: p.notes || "" })));
  const openLightbox = (path) => {
    const idx = allScreenshots.findIndex((s) => s.path === path);
    setLightbox(idx >= 0 ? idx : 0);
  };
  const settingsPhases = trade.custom_phases?.length ? trade.custom_phases : (settings?.default_phases || []);
  // Fallback to guarantee the two idea phases are always available even if
  // the user's settings somehow lack them.
  const availablePhases = settingsPhases.length
    ? settingsPhases
    : ["Idea - Initial", "Idea - Update"];
  const pnlColor = trade.pnl > 0 ? "text-emerald-500" : trade.pnl < 0 ? "text-rose-500" : "text-zinc-400";

  // ------------- Trade Metrics mode (per-user, per-trade) -------------
  // Three modes:
  //   "trade"      → use whatever is saved on the trade record
  //   "my_default" → use the user's Settings defaults (default_account_size + default_trade_size_pct)
  //   "my_custom"  → per-user, per-trade custom overrides
  // Resolution: explicit pref.mode > implicit from Settings toggle > "trade".
  const useDefaultSize = settings?.use_default_trade_size !== false;
  const implicitMode = useDefaultSize ? "my_default" : "trade";
  const mode = metricsPref?.mode || implicitMode;

  const effectiveTrade = (() => {
    if (mode === "my_default") {
      const acct = settings?.default_account_size;
      const pct = settings?.default_trade_size_pct;
      if (acct != null && pct != null) {
        const entry = trade.entry_price;
        return {
          ...trade,
          account_size: acct,
          position_size_pct: pct,
          shares: entry ? ((pct / 100) * acct) / entry : trade.shares,
          _size_source: "my_default",
        };
      }
      return trade;
    }
    if (mode === "my_custom") {
      const acct = metricsPref?.custom_account_size;
      const pct = metricsPref?.custom_size_pct;
      if (acct != null && pct != null) {
        const entry = trade.entry_price;
        return {
          ...trade,
          account_size: acct,
          position_size_pct: pct,
          shares: entry ? ((pct / 100) * acct) / entry : trade.shares,
          _size_source: "my_custom",
        };
      }
      return { ...trade, _size_source: "my_custom_incomplete" };
    }
    return { ...trade, _size_source: "trade" };
  })();
  const accountForSizing = effectiveTrade.account_size ?? settings?.default_account_size;

  const saveMetricsPref = async (patch) => {
    const next = {
      mode: patch.mode ?? mode,
      custom_account_size: patch.custom_account_size !== undefined ? patch.custom_account_size : (metricsPref?.custom_account_size ?? null),
      custom_size_pct: patch.custom_size_pct !== undefined ? patch.custom_size_pct : (metricsPref?.custom_size_pct ?? null),
    };
    setMetricsPref((prev) => ({ ...(prev || {}), ...next }));
    try {
      const { data } = await api.put(`/trades/${id}/metrics-pref`, next);
      setMetricsPref(data);
    } catch (e) {
      toast.error("Could not save metrics preference");
    }
  };

  const openPhaseDialog = async () => {
    // Refetch settings in case they were edited in another tab so the dropdown is current.
    let tfList = settings?.timeframes || [];
    try {
      const { data } = await api.get("/settings");
      setSettings(data);
      tfList = data.timeframes || tfList;
    } catch { /* ignore */ }
    const defaultTf = tfList.includes(trade.timeframe) ? trade.timeframe : (tfList.includes("1D") ? "1D" : (tfList[0] || "1D"));
    // Carry over Confidence from the most recent phase for continuity.
    const lastPhase = trade.phases?.length ? trade.phases[trade.phases.length - 1] : null;
    // Confluence criteria: union of every criterion selected across ALL prior phases
    // of this trade, de-duplicated while preserving first-seen order.
    const seen = new Set();
    const priorCriteria = [];
    for (const p of trade.phases || []) {
      for (const c of p.criteria || []) {
        if (c && !seen.has(c)) { seen.add(c); priorCriteria.push(c); }
      }
    }
    // Auto-fill Price with the currently loaded live quote if we have one.
    const livePrice = quote?.price != null ? String(quote.price.toFixed(2)) : "";
    setNewPhase({
      phase: "Idea - Update",
      notes: "",
      timeframe: defaultTf,
      criteria: priorCriteria,
      screenshots: [],
      price: livePrice,
      shares: "",
      confidence: lastPhase?.confidence ?? null,
      created_at_override: "",
    });
    setPhaseOpen(true);
  };

  return (
    <div className="p-8">
      <button onClick={() => nav(-1)} className="flex items-center gap-1 text-zinc-500 hover:text-zinc-300 text-sm mb-4" data-testid="back-btn"><ArrowLeft className="w-4 h-4" /> Back</button>

      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-mono font-bold text-3xl tracking-tight" data-testid="trade-ticker">{trade.ticker}</h1>
            <span className={`text-xs uppercase tracking-wider px-2 py-0.5 rounded-sm border font-mono ${
              trade.status === "open" ? "border-blue-500/30 text-blue-400 bg-blue-500/10" :
              trade.status === "closed" ? "border-zinc-700 text-zinc-400" :
              "border-amber-500/30 text-amber-400 bg-amber-500/10"
            }`} data-testid="trade-status-badge">{trade.status === "idea" && trade.current_phase ? trade.current_phase : trade.status}</span>
            <span className="text-xs uppercase tracking-wider px-2 py-0.5 rounded-sm border border-zinc-800 font-mono text-zinc-400">{trade.direction}</span>
            {trade.logarithmic && (
              <span className="text-xs uppercase tracking-wider px-2 py-0.5 rounded-sm border border-blue-500/30 bg-blue-500/10 text-blue-400 font-mono" data-testid="log-badge">Log</span>
            )}
          </div>
          <div className="flex items-center gap-6 mt-3 text-xs">
            <div className="flex items-center gap-2" data-testid="confidence-stars">
              <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono w-20">Confidence</span>
              <div className="flex gap-0.5">
                {[1,2,3,4,5].map((n) => <Star key={n} className={`w-4 h-4 ${n <= (trade.current_confidence || 0) ? "fill-blue-500 text-blue-500" : "text-zinc-800"}`} />)}
              </div>
            </div>
            <div className="flex items-center gap-2" data-testid="confluence-stars">
              <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono w-20">Confluence</span>
              <div className="flex gap-0.5">
                {[1,2,3,4,5].map((n) => <Star key={n} className={`w-4 h-4 ${n <= (trade.confluence || 0) ? "fill-emerald-500 text-emerald-500" : "text-zinc-800"}`} />)}
              </div>
            </div>
          </div>
          <p className="text-xs text-zinc-500 mt-2 font-mono">
            {trade.timeframe} · Opened {new Date(trade.created_at).toLocaleDateString()}
            {canEditExistingDates(me) && (
              <DateEditor
                value={trade.created_at}
                testIdPrefix="trade-date-editor"
                onSave={async (iso) => {
                  await api.patch(`/trades/${trade.id}/created-at`, { created_at: iso });
                  await load();
                  toast.success("Trade date updated");
                }}
              />
            )}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <TradeReactions
            trade={trade}
            onUpdated={(r) => setTrade((prev) => prev ? { ...prev, ...r } : prev)}
          />
          <Button onClick={refreshPrice} data-testid="refresh-price-btn" title="Refresh live quote" className="rounded-sm bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 text-zinc-200 gap-2 h-8 font-mono">
            <RefreshCw className="w-3.5 h-3.5" />
            {quote?.price != null ? (
              <span className="inline-flex items-baseline gap-1.5">
                <span className="font-semibold">${quote.price.toFixed(2)}</span>
                {quote.change_pct != null && (
                  <span className={quote.change >= 0 ? "text-emerald-400" : "text-rose-400"}>
                    {quote.change >= 0 ? "+" : ""}{quote.change_pct.toFixed(2)}%
                  </span>
                )}
              </span>
            ) : (
              <span>Live Price</span>
            )}
          </Button>
          <Button
            onClick={() => window.open(`https://www.tradingview.com/chart/?symbol=${encodeURIComponent(trade.ticker)}`, "_blank", "noopener,noreferrer")}
            data-testid="open-tradingview-btn"
            title="Open in TradingView (uses installed desktop app on Windows, otherwise web chart)"
            className="rounded-sm bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 text-zinc-200 gap-2 h-8"
          >
            <ExternalLink className="w-3.5 h-3.5" /> TradingView
          </Button>
          {canEditTrade(me, trade) && (
            <Button onClick={() => setEditing(!editing)} data-testid="edit-btn" className="rounded-sm bg-blue-600 hover:bg-blue-500 h-8">{editing ? "Cancel" : "Edit"}</Button>
          )}
          {canDeleteTrade(me, trade) && (
            <Button onClick={removeTrade} data-testid="delete-trade-btn" className="rounded-sm bg-rose-600/20 border border-rose-800 hover:bg-rose-600/30 text-rose-400 h-8"><Trash2 className="w-4 h-4" /></Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_20.7rem] gap-4">
        {/* LEFT: Timeline */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading text-sm uppercase tracking-wider text-zinc-400">Phase Timeline</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPhaseSort((s) => (s === "asc" ? "desc" : "asc"))}
                data-testid="phase-sort-toggle"
                className="flex items-center gap-1.5 px-2 py-1 border border-zinc-800 rounded-sm text-[11px] font-mono uppercase tracking-wider text-zinc-400 hover:text-zinc-100 hover:border-zinc-700 h-8"
              >
                <ArrowUpDown className="w-3 h-3" />
                {phaseSort === "asc" ? "Oldest first" : "Newest first"}
              </button>
              {canAddPhase(me) && (
                <Button onClick={openPhaseDialog} data-testid="add-phase-btn" size="sm" className="rounded-sm bg-blue-600 hover:bg-blue-500 h-8 gap-1"><Plus className="w-3.5 h-3.5" /> Add Phase</Button>
              )}
              {canAddPhase(me) && trade?.status !== "closed" && (
                <Button
                  onClick={() => setCloseDialogOpen(true)}
                  data-testid="close-trade-btn"
                  size="sm"
                  className="rounded-sm bg-zinc-900 border border-rose-500/40 hover:bg-rose-500/10 text-rose-400 hover:text-rose-300 h-8 gap-1"
                >
                  <X className="w-3.5 h-3.5" /> Close
                </Button>
              )}
            </div>
          </div>

          {phases.length === 0 && (
            <div className="border border-zinc-900 bg-[#0a0a0a] rounded-sm p-10 text-center text-sm text-zinc-500">
              No phase entries yet. Log the first phase to start your trade timeline.
            </div>
          )}

          <div className="space-y-3">
            {sortedPhases.map((p) => {
              const key = (p.phase || "").toLowerCase();
              const category =
                /idea/.test(key) ? "idea" :
                /entry|management|managing|trimmed/.test(key) ? "open" :
                /dead/.test(key) ? "dead" :
                /closed|exited/.test(key) ? "closed" : "other";
              const railBg = {
                idea: "bg-blue-500",
                open: "bg-emerald-500",
                dead: "bg-rose-500",
                closed: "bg-zinc-500",
                other: "bg-zinc-700",
              }[category];
              const phaseTone = {
                idea: "text-blue-400",
                open: "text-emerald-400",
                dead: "text-rose-400",
                closed: "text-zinc-300",
                other: "text-zinc-300",
              }[category];
              const positionPct = phasePositionPct(phases, trade?.direction, p);
              return (
                <div key={p.id} className="flex" data-testid={`phase-${p.phase}`}>
                  <div className={`w-px shrink-0 rounded-sm ${railBg}`} />
                  <div className="flex-1 border border-zinc-900 bg-[#0a0a0a] rounded-sm p-4 ml-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`font-mono font-semibold uppercase text-sm ${phaseTone}`}>{p.phase}</span>
                      {p.price != null && (
                        <span className="text-xs font-mono text-zinc-300" data-testid={`phase-price-${p.id}`}>
                          {positionPct != null && (
                            <span className="text-zinc-400" data-testid={`phase-position-pct-${p.id}`}>{positionPct}% </span>
                          )}
                          @ ${Number(p.price).toFixed(2)}
                        </span>
                      )}
                      {p.timeframe && <span className="text-[10px] px-1.5 py-0.5 border border-zinc-800 rounded-sm font-mono text-zinc-500">{p.timeframe}</span>}
                      {p.confidence != null && (
                        <span className="inline-flex items-center gap-0.5" data-testid={`phase-confidence-${p.id}`}>
                          {[1,2,3,4,5].map((n) => <Star key={n} className={`w-3 h-3 ${n <= p.confidence ? "fill-blue-500 text-blue-500" : "text-zinc-800"}`} />)}
                        </span>
                      )}
                      <span className="inline-flex items-center gap-0.5" data-testid={`phase-confluence-${p.id}`}>
                        {[1,2,3,4,5].map((n) => <Star key={n} className={`w-3 h-3 ${n <= Math.min(5, (p.criteria || []).length) ? "fill-emerald-500 text-emerald-500" : "text-zinc-800"}`} />)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-zinc-300 font-mono">
                        {new Date(p.created_at).toLocaleString()}
                        {canEditExistingDates(me) && (
                          <DateEditor
                            value={p.created_at}
                            testIdPrefix={`phase-date-editor-${p.id}`}
                            onSave={async (iso) => {
                              await api.patch(`/trades/${trade.id}/phases/${p.id}/created-at`, { created_at: iso });
                              await load();
                              toast.success("Phase date updated");
                            }}
                          />
                        )}
                      </span>
                      {canAddPhase(me) && (
                        <button onClick={() => removePhase(p.id)} className="text-zinc-600 hover:text-rose-500" data-testid={`remove-phase-${p.id}`}><X className="w-3.5 h-3.5" /></button>
                      )}
                    </div>
                  </div>
                  {p.notes && <p className="text-sm text-zinc-300 whitespace-pre-wrap mb-2">{p.notes}</p>}
                  {p.criteria?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {p.criteria.map((c) => <span key={c} className="text-[10px] px-1.5 py-0.5 border border-zinc-800 rounded-sm text-zinc-400 font-mono">{c}</span>)}
                    </div>
                  )}
                  {p.screenshots?.length > 0 && (
                    <div className="grid grid-cols-3 md:grid-cols-4 gap-2 mt-2">
                      {p.screenshots.map((s) => (
                        <button key={s} onClick={() => openLightbox(s)} className="aspect-video overflow-hidden border border-zinc-800 rounded-sm bg-black hover:border-blue-500 transition-colors">
                          <AuthImg path={s} className="w-full h-full object-contain" />
                        </button>
                      ))}
                    </div>
                  )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* RIGHT: Metrics */}
        <div className="space-y-4 lg:sticky lg:top-0 lg:self-start lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto lg:pr-1" data-testid="trade-metrics-panel">
          <div className="border border-zinc-900 bg-[#0a0a0a] rounded-sm p-4">
            <div className="flex items-center justify-between mb-3 gap-2">
              <button
                type="button"
                onClick={() => setSetupCollapsed((v) => !v)}
                data-testid="trade-setup-toggle"
                className="inline-flex items-center gap-2 text-zinc-400 hover:text-zinc-200"
              >
                <Gauge className="w-4 h-4 text-blue-400" />
                <h3 className="font-heading text-sm uppercase tracking-wider">Trade Setup</h3>
                {setupCollapsed ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5" />}
              </button>
              {!setupCollapsed && (
              <Select
                value={mode}
                onValueChange={(v) => {
                  if (v === "my_custom") {
                    setCustomDraft({
                      account: metricsPref?.custom_account_size ?? (settings?.default_account_size ?? ""),
                      pct: metricsPref?.custom_size_pct ?? (settings?.default_trade_size_pct ?? ""),
                    });
                    setCustomDialogOpen(true);
                  } else {
                    saveMetricsPref({ mode: v });
                  }
                }}
              >
                <SelectTrigger data-testid="metrics-mode-select" className="h-7 w-[140px] bg-zinc-950 border-zinc-800 rounded-sm font-mono text-[10px] uppercase tracking-wider text-zinc-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800 rounded-sm text-xs">
                  <SelectItem value="trade" data-testid="metrics-mode-trade">Trade Default</SelectItem>
                  <SelectItem value="my_default" data-testid="metrics-mode-my-default">My Default</SelectItem>
                  <SelectItem value="my_custom" data-testid="metrics-mode-my-custom">My Custom</SelectItem>
                </SelectContent>
              </Select>
              )}
            </div>
            {!setupCollapsed && (
              <>
            {!editing ? (
              <dl className="space-y-2 text-sm">
                <Row label="Entry Range" value={trade.entry_range_low != null || trade.entry_range_high != null ? `$${trade.entry_range_low ?? "—"} – $${trade.entry_range_high ?? "—"}` : "—"} />
                <Row label="Stop" value={trade.stop_loss != null ? `$${trade.stop_loss}` : "—"} tone="neg" />
                {(() => {
                  const entry = effectiveTrade.entry_price;
                  const stop = effectiveTrade.stop_loss;
                  const shares = effectiveTrade.shares;
                  if (entry == null || stop == null || !shares) return null;
                  const dir = effectiveTrade.direction || "long";
                  const perShare = dir === "long" ? (entry - stop) : (stop - entry);
                  const maxLoss = perShare * shares; // positive dollar amount when trade is profitable-side setup
                  if (maxLoss <= 0) return null;
                  const est = computeEstimatedPnl(effectiveTrade);
                  if (!est || est.pnl <= 0) return null;
                  const rr = est.pnl / maxLoss;
                  const tone = rr >= 2 ? "text-emerald-400" : rr < 1 ? "text-rose-400" : "text-zinc-200";
                  return (
                    <div className="flex justify-between" data-testid="rr-row">
                      <span className="text-zinc-500">R:R</span>
                      <span className={`font-mono ${tone}`}>1 : {rr.toFixed(2)}</span>
                    </div>
                  );
                })()}
                {effectiveTrade.take_profit_levels?.length > 0 ? (
                  effectiveTrade.take_profit_levels.map((lv, i) => {
                    const size = lv.size_pct != null ? Number(lv.size_pct) : null;
                    const sharesAtLevel = (size != null && effectiveTrade.shares) ? Math.round(Math.abs(effectiveTrade.shares) * (size / 100)) : null;
                    const parts = [`$${lv.price}`];
                    if (size != null) parts.push(`${size}%`);
                    if (sharesAtLevel != null) parts.push(`${sharesAtLevel} sh`);
                    return <Row key={i} label={`TP${i + 1}`} value={parts.join(" · ")} tone="pos" />;
                  })
                ) : (
                  <Row label="Target" value={trade.take_profit != null ? `$${trade.take_profit}` : "—"} tone="pos" />
                )}

                <div className="border-t border-zinc-900 pt-2 mt-2 space-y-2">
                  <Row
                    label="Trade Size % of Account"
                    value={effectiveTrade.position_size_pct != null
                      ? (effectiveTrade._size_source === "my_default" || effectiveTrade._size_source === "my_custom"
                          ? <span className="inline-flex items-center gap-1.5">{`${effectiveTrade.position_size_pct}%`}<span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm font-mono border ${effectiveTrade._size_source === "my_custom" ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400" : "border-blue-500/30 bg-blue-500/10 text-blue-400"}`} data-testid="size-source-tag">{effectiveTrade._size_source === "my_custom" ? "custom" : "default"}</span></span>
                          : `${effectiveTrade.position_size_pct}%`)
                      : "—"}
                  />
                  <Row label="Trade Size # of Shares" value={effectiveTrade.shares != null ? Math.round(Math.abs(effectiveTrade.shares)) : "—"} />
                  <Row label="Account Value at Entry" value={effectiveTrade.account_size != null ? `$${effectiveTrade.account_size.toLocaleString()}` : (accountForSizing != null ? `$${accountForSizing.toLocaleString()}` : "—")} />
                </div>

                {(() => {
                  const est = computeEstimatedPnl(effectiveTrade);
                  const hasEst = !!est;
                  const hasActual = trade.pnl != null;
                  if (!hasEst && !hasActual) return null;
                  const estTone = est && est.pnl >= 0 ? "text-emerald-500" : "text-rose-500";
                  return (
                    <div className="border-t border-zinc-900 pt-2 mt-2 space-y-1" data-testid="pnl-section">
                      {hasEst && (
                        <>
                          <div className="flex justify-between">
                            <span className="text-zinc-500">
                              Est. Profit on Trade
                              {est.allocated_pct !== 100 && (
                                <span className="text-[10px] text-zinc-600 ml-1 font-mono">({est.allocated_pct.toFixed(0)}%)</span>
                              )}
                            </span>
                            <span className={`font-mono font-semibold ${estTone}`}>{fmtMoney(est.pnl)}</span>
                          </div>
                          {est.pnl_pct != null && (
                            <div className="flex justify-between">
                              <span className="text-zinc-500">Est. Profit % on Account</span>
                              <span className={`font-mono ${estTone}`}>{est.pnl_pct.toFixed(2)}%</span>
                            </div>
                          )}
                          {est.pnl_pct_of_trade != null && (
                            <div className="flex justify-between">
                              <span className="text-zinc-500">Est. Profit % on Trade</span>
                              <span className={`font-mono ${estTone}`}>{est.pnl_pct_of_trade.toFixed(2)}%</span>
                            </div>
                          )}
                          {(() => {
                            const entry = trade.entry_price;
                            const stop = trade.stop_loss;
                            const sh = trade.shares;
                            const account = trade.account_size;
                            if (entry == null || stop == null || !sh || !account) return null;
                            const dir = trade.direction || "long";
                            const perShare = dir === "long" ? (stop - entry) : (entry - stop);
                            const maxLoss = perShare * sh;
                            const maxLossPct = (maxLoss / account) * 100;
                            return (
                              <div className="flex justify-between" data-testid="max-loss-row">
                                <span className="text-zinc-500">Est. Max Loss % on Account</span>
                                <span className="font-mono text-rose-500">{maxLossPct.toFixed(2)}%</span>
                              </div>
                            );
                          })()}
                        </>
                      )}
                      {hasActual && (
                        <div className={`${hasEst ? "border-t border-zinc-900 pt-2 mt-2" : ""} space-y-1`}>
                          <div className="flex justify-between"><span className="text-zinc-500">P&L</span><span className={`font-mono font-semibold ${pnlColor}`}>{fmtMoney(trade.pnl)}</span></div>
                          <div className="flex justify-between"><span className="text-zinc-500">P&L %</span><span className={`font-mono ${pnlColor}`}>{trade.pnl_pct != null ? `${trade.pnl_pct.toFixed(2)}%` : "—"}</span></div>
                          <div className="flex justify-between"><span className="text-zinc-500">R</span><span className={`font-mono ${pnlColor}`}>{trade.r_multiple != null ? `${trade.r_multiple.toFixed(2)}R` : "—"}</span></div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                <div className="border-t border-zinc-900 pt-2 mt-2">
                  <Row label="Scale" value={trade.logarithmic ? "Logarithmic" : "Linear"} />
                </div>
              </dl>
            ) : (
              <div className="space-y-2">
                <EditField label="Range Lo" value={edit.entry_range_low} onChange={(v) => setEdit({...edit, entry_range_low: v})} />
                <EditField label="Range Hi" value={edit.entry_range_high} onChange={(v) => setEdit({...edit, entry_range_high: v})} />
                <EditField label="Stop" value={edit.stop_loss} onChange={(v) => setEdit({...edit, stop_loss: v})} />

                <div className="pt-1">
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-xs uppercase tracking-wider text-zinc-500">TP Levels</Label>
                    <button type="button" onClick={() => setEdit({ ...edit, take_profit_levels: [...(edit.take_profit_levels || []), { price: "", size_pct: "" }] })} data-testid="edit-add-tp-btn" className="text-[11px] text-blue-400 hover:text-blue-300 flex items-center gap-1 font-mono uppercase tracking-wider">
                      <Plus className="w-3 h-3" /> Add
                    </button>
                  </div>
                  <div className="space-y-1.5">
                    {(edit.take_profit_levels || []).map((lv, i) => (
                      <div key={i} className="grid grid-cols-[auto_1fr_1fr_auto] gap-1.5 items-center">
                        <span className="text-[10px] font-mono text-zinc-500 uppercase w-8">TP{i + 1}</span>
                        <Input type="number" step="0.01" placeholder="Price" value={lv.price}
                          onChange={(e) => setEdit({ ...edit, take_profit_levels: edit.take_profit_levels.map((x, idx) => idx === i ? { ...x, price: e.target.value } : x) })}
                          data-testid={`edit-tp-price-${i}`} className="bg-zinc-950 border-zinc-800 rounded-sm h-8 font-mono" />
                        <Input type="number" step="1" placeholder="Size %" value={lv.size_pct}
                          onChange={(e) => setEdit({ ...edit, take_profit_levels: edit.take_profit_levels.map((x, idx) => idx === i ? { ...x, size_pct: e.target.value } : x) })}
                          data-testid={`edit-tp-size-${i}`} className="bg-zinc-950 border-zinc-800 rounded-sm h-8 font-mono" />
                        <button type="button" onClick={() => setEdit({ ...edit, take_profit_levels: edit.take_profit_levels.filter((_, idx) => idx !== i) })} data-testid={`edit-remove-tp-${i}`} className="text-zinc-600 hover:text-rose-500">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <label className="flex items-center gap-2 cursor-pointer text-sm text-zinc-300 pt-1">
                  <Checkbox
                    checked={!!edit.logarithmic}
                    onCheckedChange={(v) => setEdit({ ...edit, logarithmic: !!v })}
                    data-testid="edit-logarithmic-checkbox"
                    className="rounded-sm border-zinc-700 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                  />
                  <span>Logarithmic</span>
                </label>

                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-500">Status</Label>
                  <Select value={edit.status} onValueChange={(v) => setEdit({...edit, status: v})}>
                    <SelectTrigger data-testid="edit-status-select" className="bg-zinc-950 border-zinc-800 rounded-sm h-8 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-zinc-950 border-zinc-800">
                      <SelectItem value="idea">Idea</SelectItem>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={saveEdits} data-testid="save-edits-btn" className="w-full rounded-sm bg-blue-600 hover:bg-blue-500 h-8 mt-2">Save</Button>
              </div>
            )}
              </>
            )}
          </div>

          {fundamentals && (
            <div className="border border-zinc-900 bg-[#0a0a0a] rounded-sm p-4" data-testid="fundamentals-card">
              <button
                type="button"
                onClick={() => setFundCollapsed((v) => !v)}
                data-testid="fundamentals-toggle"
                className={`w-full inline-flex items-center gap-2 text-zinc-400 hover:text-zinc-200 ${fundCollapsed ? "" : "mb-3"}`}
              >
                <Building2 className="w-4 h-4 text-blue-400" />
                <h3 className="font-heading text-sm uppercase tracking-wider">Fundamentals</h3>
                {fundCollapsed ? <ChevronDown className="w-3.5 h-3.5 ml-auto" /> : <ChevronUp className="w-3.5 h-3.5 ml-auto" />}
              </button>
              {!fundCollapsed && (
              <dl className="space-y-2 text-sm">
                <Row label="Market Cap" value={fmtCompact(fundamentals.market_cap, "$")} />
                <Row label="P/E Ratio" value={fundamentals.pe_ratio != null ? fundamentals.pe_ratio.toFixed(2) : "—"} />
                <Row label="Float" value={fmtCompact(fundamentals.float_shares)} />
                <Row label="Short % of Float" value={fundamentals.short_float_pct != null ? `${fundamentals.short_float_pct.toFixed(2)}%` : "—"} />
                <Row
                  label="Beta (5Y)"
                  value={(() => {
                    const b = fundamentals.beta;
                    if (b == null) return "—";
                    // Colour the value so a glance tells you how the stock moves
                    // vs the market: green = calmer than S&P, rose = jumpier.
                    const tone = b > 1.5 ? "text-rose-400" : b < 0.8 ? "text-emerald-400" : "text-zinc-200";
                    return (
                      <span className={`font-mono ${tone}`} data-testid="beta-value" title="5-year monthly beta vs S&P 500 · <0.8 calmer, 0.8–1.5 in-line, >1.5 jumpier">
                        {b.toFixed(2)}
                      </span>
                    );
                  })()}
                />
                <Row
                  label="Next Earnings"
                  value={(() => {
                    if (!fundamentals.next_earnings_at) return "—";
                    const d = new Date(fundamentals.next_earnings_at);
                    if (isNaN(d)) return "—";
                    const dateStr = d.toLocaleString(undefined, {
                      month: "short", day: "numeric", year: "numeric",
                      hour: "numeric", minute: "2-digit",
                    });
                    return (
                      <span className="inline-flex items-center gap-1.5" data-testid="next-earnings-value">
                        {dateStr}
                        {fundamentals.next_earnings_estimated && (
                          <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-amber-500/30 bg-amber-500/10 text-amber-400 font-mono">est</span>
                        )}
                      </span>
                    );
                  })()}
                />
              </dl>
              )}
            </div>
          )}

          <BusinessQualityChecklist trade={trade} onTradeUpdate={(t) => setTrade(t)} />

          <InsiderSummary ticker={trade.ticker} />
          <MarketHistory trade={trade} onUpdated={(t) => setTrade(t)} />
        </div>
      </div>


      {/* Add phase dialog */}
      <Dialog open={phaseOpen} onOpenChange={setPhaseOpen}>
        <DialogContent className="bg-[#0a0a0a] border-zinc-900 max-w-lg max-h-[90vh] p-0 gap-0 flex flex-col">
          <div className="px-6 pt-5 pb-3 border-b border-zinc-900 shrink-0">
            <DialogTitle className="font-heading text-lg font-semibold">New Phase Entry</DialogTitle>
          </div>
          <div className="space-y-3 overflow-y-auto flex-1 px-6 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Phase</Label>
                <Select value={newPhase.phase} onValueChange={(v) => {
                  const patch = { ...newPhase, phase: v };
                  // When user picks "Closed", auto-fill shares with the entire
                  // current position — the trade is being fully closed via FIFO.
                  if (v === "Closed") {
                    patch.shares = String(Math.abs(currentPosition.position || 0));
                  }
                  setNewPhase(patch);
                }}>
                  <SelectTrigger data-testid="phase-select" className="bg-zinc-950 border-zinc-800 rounded-sm mt-1"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800">
                    {availablePhases.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Timeframe</Label>
                <Select value={newPhase.timeframe} onValueChange={(v) => setNewPhase({...newPhase, timeframe: v})}>
                  <SelectTrigger data-testid="phase-timeframe-select" className="bg-zinc-950 border-zinc-800 rounded-sm mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800">
                    {(settings?.timeframes || ["1H","2H","4H","8H","1D","2D","3D","4D","Weekly","Monthly"]).map((tf) => <SelectItem key={tf} value={tf}>{tf}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Price at phase</Label>
              <Input type="number" step="0.01" value={newPhase.price} onChange={(e) => setNewPhase({...newPhase, price: e.target.value})} data-testid="phase-price-input" className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm font-mono" />
            </div>
            {POSITION_PHASES.has(newPhase.phase) && (
              <div className="border border-blue-500/20 bg-blue-500/5 rounded-sm p-3 space-y-2" data-testid="phase-position-block">
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-wider text-blue-400">
                    {BUY_PHASES.has(newPhase.phase)
                      ? "Shares Bought"
                      : (newPhase.phase === "Closed" ? "Shares Closed (Full Position, FIFO)" : "Shares Sold")}
                    <span className="text-rose-400 ml-1">*</span>
                  </Label>
                  <div className="text-[10px] font-mono text-zinc-500">
                    <span className="text-zinc-600 uppercase tracking-wider">Now:</span> <span className="text-zinc-300" data-testid="phase-position-current">{currentPosition.position !== 0 ? `${Math.abs(currentPosition.position).toLocaleString()} sh` : "0 sh"}</span>
                    {currentPosition.position !== 0 && (
                      <> <span className="text-zinc-700 mx-1">·</span> <span className="text-zinc-600 uppercase tracking-wider">Avg:</span> <span className="text-zinc-300" data-testid="phase-position-avg-cost">${currentPosition.avgCost.toFixed(4)}</span></>
                    )}
                  </div>
                </div>
                <Input
                  type="number"
                  step="any"
                  min="0"
                  value={newPhase.shares}
                  onChange={(e) => setNewPhase({ ...newPhase, shares: e.target.value })}
                  data-testid="phase-shares-input"
                  placeholder="e.g. 100"
                  readOnly={newPhase.phase === "Closed"}
                  className={`bg-zinc-950 border-zinc-800 rounded-sm font-mono ${newPhase.phase === "Closed" ? "opacity-70 cursor-not-allowed" : ""}`}
                />
                {newPhase.phase === "Closed" && (
                  <p className="text-[11px] text-zinc-500 font-mono" data-testid="phase-closed-hint">
                    {currentPosition.position !== 0
                      ? `Closes the full remaining ${Math.abs(currentPosition.position).toLocaleString()} sh at the price above.`
                      : "Nothing left to close — add Open - Entry / Added first, or pick Closed - Update for a post-close annotation."}
                  </p>
                )}
                {SELL_PHASES.has(newPhase.phase) && newPhase.phase !== "Closed" && newPhase.shares !== "" && parseFloat(newPhase.shares) > Math.abs(currentPosition.position) + 1e-9 && (
                  <p className="text-[11px] text-rose-400 font-mono" data-testid="phase-shares-too-many">
                    Trimming {newPhase.shares} sh exceeds current position of {Math.abs(currentPosition.position)} sh
                  </p>
                )}
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Confidence <span className="text-zinc-600 normal-case">(updates the trade)</span></Label>
                <div className="mt-2 flex items-center gap-1" data-testid="phase-confidence-input">
                  {[1,2,3,4,5].map((n) => (
                    <button key={n} type="button" onClick={() => setNewPhase({ ...newPhase, confidence: newPhase.confidence === n ? null : n })} data-testid={`phase-confidence-${n}`}>
                      <Star className={`w-6 h-6 transition-colors ${newPhase.confidence != null && n <= newPhase.confidence ? "fill-blue-500 text-blue-500" : "text-zinc-700"}`} />
                    </button>
                  ))}
                  {newPhase.confidence != null && (
                    <button type="button" onClick={() => setNewPhase({ ...newPhase, confidence: null })} className="ml-2 text-[10px] text-zinc-500 hover:text-zinc-300 font-mono uppercase tracking-wider" data-testid="phase-confidence-clear">Clear</button>
                  )}
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Confluence <span className="text-zinc-600 normal-case">(from criteria)</span></Label>
                <div className="mt-2 flex items-center gap-1" data-testid="phase-confluence-preview">
                  {[1,2,3,4,5].map((n) => (
                    <Star key={n} className={`w-6 h-6 ${n <= Math.min(5, newPhase.criteria.length) ? "fill-emerald-500 text-emerald-500" : "text-zinc-700"}`} />
                  ))}
                </div>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Notes</Label>
              <Textarea rows={5} value={newPhase.notes} onChange={(e) => setNewPhase({...newPhase, notes: e.target.value})} data-testid="phase-notes-input" className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm" />
            </div>
            {settings?.setup_criteria_templates?.length > 0 && (
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Criteria observed</Label>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {settings.setup_criteria_templates.map((c) => (
                    <label key={c} className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
                      <Checkbox checked={newPhase.criteria.includes(c)} onCheckedChange={() => setNewPhase((p) => ({ ...p, criteria: p.criteria.includes(c) ? p.criteria.filter((x) => x !== c) : [...p.criteria, c] }))} className="rounded-sm border-zinc-700 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600" />
                      {c}
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Screenshots</Label>
              <div className="mt-1">
                <ScreenshotUploader
                  value={newPhase.screenshots}
                  onChange={(next) => setNewPhase((p) => ({ ...p, screenshots: next }))}
                  pasteScope="global"
                  testIdPrefix="phase-screenshot"
                />
              </div>
            </div>
            {canOverrideDates(me) && (
              <div className="border border-amber-500/30 bg-amber-500/5 rounded-sm p-2">
                <Label className="text-[10px] uppercase tracking-wider text-amber-400 font-mono">Admin · Override Date</Label>
                <Input
                  type="datetime-local"
                  value={newPhase.created_at_override}
                  onChange={(e) => setNewPhase({ ...newPhase, created_at_override: e.target.value })}
                  data-testid="phase-created-at-override"
                  className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm font-mono h-9"
                />
                <p className="text-[10px] text-zinc-600 mt-1">Leave blank to use the current timestamp.</p>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 px-6 py-3 border-t border-zinc-900 shrink-0 bg-[#0a0a0a]">
            <Button variant="outline" onClick={() => setPhaseOpen(false)} className="rounded-sm border-zinc-800 bg-transparent">Cancel</Button>
            <Button onClick={addPhase} data-testid="submit-phase-btn" className="rounded-sm bg-blue-600 hover:bg-blue-500">Save Phase</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Close Trade dialog */}
      {trade && (
        <CloseTradeDialog
          open={closeDialogOpen}
          onOpenChange={setCloseDialogOpen}
          trade={trade}
          onClosed={() => load()}
        />
      )}

      {/* Custom metrics dialog */}
      <Dialog open={customDialogOpen} onOpenChange={setCustomDialogOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 rounded-sm max-w-md" data-testid="metrics-custom-dialog">
          <DialogTitle className="font-heading text-lg text-zinc-100">Custom Trade Setup</DialogTitle>
          <p className="text-xs text-zinc-500 -mt-1">These values apply only to this trade for you. They won't affect other users or the trade's stored values.</p>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Account Value at Entry</Label>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-zinc-500 font-mono text-sm">$</span>
                <Input
                  type="number"
                  step="1"
                  autoFocus
                  value={customDraft.account}
                  onChange={(e) => setCustomDraft((d) => ({ ...d, account: e.target.value }))}
                  data-testid="metrics-custom-account-input"
                  className="bg-zinc-950 border-zinc-800 rounded-sm h-9 font-mono"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Trade Size % of Account</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  value={customDraft.pct}
                  onChange={(e) => setCustomDraft((d) => ({ ...d, pct: e.target.value }))}
                  data-testid="metrics-custom-pct-input"
                  className="bg-zinc-950 border-zinc-800 rounded-sm h-9 font-mono"
                />
                <span className="text-zinc-500 font-mono text-sm">%</span>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              onClick={() => setCustomDialogOpen(false)}
              data-testid="metrics-custom-cancel"
              className="rounded-sm bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 text-zinc-300 h-9"
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                const acct = customDraft.account === "" ? null : parseFloat(customDraft.account);
                const pct = customDraft.pct === "" ? null : parseFloat(customDraft.pct);
                if (acct == null || pct == null || isNaN(acct) || isNaN(pct)) {
                  toast.error("Please enter both a valid account size and %");
                  return;
                }
                await saveMetricsPref({ mode: "my_custom", custom_account_size: acct, custom_size_pct: pct });
                setCustomDialogOpen(false);
                toast.success("Custom metrics saved");
              }}
              data-testid="metrics-custom-submit"
              className="rounded-sm bg-blue-600 hover:bg-blue-500 h-9"
            >
              Submit
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Lightbox */}
      <Lightbox
        items={allScreenshots}
        index={lightbox}
        onClose={() => setLightbox(null)}
        onIndexChange={setLightbox}
      />
    </div>
  );
}

function Lightbox({ items, index, onClose, onIndexChange }) {
  const open = index != null && items.length > 0;
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const containerRef = useRef(null);
  const transformRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") onIndexChange(Math.max(0, index - 1));
      else if (e.key === "ArrowRight") onIndexChange(Math.min(items.length - 1, index + 1));
      else if (e.key === "f" || e.key === "F") toggleFullscreen();
      else if (e.key === "n" || e.key === "N") setShowNotes((v) => !v);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, index, items.length]);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  const toggleFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.().catch(() => {});
    } else {
      document.exitFullscreen?.();
    }
  };

  const zoomIn = () => transformRef.current?.zoomIn?.();
  const zoomOut = () => transformRef.current?.zoomOut?.();
  const resetZoom = () => transformRef.current?.resetTransform?.();

  if (!open) return null;
  const item = items[index];

  return (
    <div ref={containerRef} className="fixed inset-0 z-50 bg-black/95 flex flex-col" data-testid="lightbox">
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-900">
        <div className="flex items-center gap-3 text-xs font-mono text-zinc-400">
          <span data-testid="lightbox-counter">{index + 1} / {items.length}</span>
          {item?.phase && <span className="text-blue-400 uppercase">{item.phase}</span>}
          {item?.created_at && <span className="text-zinc-600">{new Date(item.created_at).toLocaleString()}</span>}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-zinc-900/70 border border-zinc-800 rounded-sm p-1" data-testid="lightbox-zoom-controls">
            <button
              onClick={zoomOut}
              data-testid="lightbox-zoom-out"
              title="Zoom out (-)"
              className="p-1.5 rounded-sm text-zinc-300 hover:text-zinc-100 hover:bg-zinc-800"
            >
              <ZoomOut className="w-4 h-4" />
            </button>
            <button
              onClick={zoomIn}
              data-testid="lightbox-zoom-in"
              title="Zoom in (+)"
              className="p-1.5 rounded-sm text-zinc-300 hover:text-zinc-100 hover:bg-zinc-800"
            >
              <ZoomIn className="w-4 h-4" />
            </button>
            <button
              onClick={resetZoom}
              data-testid="lightbox-zoom-reset"
              title="Reset (0)"
              className="p-1.5 rounded-sm text-zinc-300 hover:text-zinc-100 hover:bg-zinc-800"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          </div>
          {item?.notes && (
            <button
              onClick={() => setShowNotes((v) => !v)}
              data-testid="lightbox-notes-toggle"
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm text-xs font-mono uppercase tracking-wider border transition-colors ${
                showNotes
                  ? "bg-blue-500 text-white border-blue-500 hover:bg-blue-400"
                  : "bg-amber-500/10 text-amber-300 border-amber-500/40 hover:bg-amber-500/20 hover:text-amber-200"
              }`}
              title="Toggle phase notes (N)"
            >
              <StickyNote className="w-4 h-4" />
              <span>Notes</span>
            </button>
          )}
          <button onClick={toggleFullscreen} data-testid="lightbox-fullscreen" className="p-2 rounded-sm text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900" title="Fullscreen (F)">
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
          <button onClick={onClose} data-testid="lightbox-close" className="p-2 rounded-sm text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900" title="Close (Esc)">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 relative flex items-center justify-center overflow-hidden">
        <button
          onClick={() => onIndexChange(Math.max(0, index - 1))}
          disabled={index === 0}
          data-testid="lightbox-prev"
          className="absolute left-4 z-10 p-3 rounded-sm bg-zinc-900/60 border border-zinc-800 text-zinc-300 hover:bg-zinc-800 disabled:opacity-30 disabled:cursor-not-allowed"
          title="Previous (←)"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>

        <div className="flex-1 h-full w-full flex items-center justify-center">
          <TransformWrapper
            ref={transformRef}
            key={item.path}
            initialScale={1}
            minScale={1}
            maxScale={8}
            wheel={{ step: 0.15, smoothStep: 0.005 }}
            doubleClick={{ mode: "reset" }}
            pinch={{ step: 5 }}
            limitToBounds={false}
            panning={{ velocityDisabled: true }}
          >
            <TransformComponent
              wrapperStyle={{ width: "100%", height: "100%", cursor: "grab" }}
              contentStyle={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}
            >
              <AuthImg key={item.path} path={item.path} className="max-h-[85vh] max-w-[85vw] object-contain block select-none" draggable={false} />
            </TransformComponent>
          </TransformWrapper>
        </div>

        {showNotes && item?.notes && (
          <div
            data-testid="lightbox-notes-overlay"
            className="absolute left-1/2 -translate-x-1/2 bottom-6 z-20 max-w-[80vw] max-h-[40vh] overflow-y-auto bg-black/80 backdrop-blur-sm border border-zinc-800 rounded-sm px-4 py-3 shadow-2xl"
          >
            <div className="flex items-start justify-between gap-4 mb-1">
              <span className="text-[10px] font-mono uppercase tracking-wider text-blue-400">Phase Notes</span>
              <button
                onClick={() => setShowNotes(false)}
                data-testid="lightbox-notes-close"
                className="text-zinc-500 hover:text-zinc-200 -mt-1 -mr-1"
                title="Hide notes (N)"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <p className="text-sm text-zinc-100 whitespace-pre-wrap leading-relaxed">{item.notes}</p>
          </div>
        )}

        <button
          onClick={() => onIndexChange(Math.min(items.length - 1, index + 1))}
          disabled={index === items.length - 1}
          data-testid="lightbox-next"
          className="absolute right-4 z-10 p-3 rounded-sm bg-zinc-900/60 border border-zinc-800 text-zinc-300 hover:bg-zinc-800 disabled:opacity-30 disabled:cursor-not-allowed"
          title="Next (→)"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      </div>

      {items.length > 1 && (
        <div className="border-t border-zinc-900 p-2 flex gap-2 overflow-x-auto justify-center">
          {items.map((it, i) => (
            <button
              key={it.path}
              onClick={() => onIndexChange(i)}
              data-testid={`lightbox-thumb-${i}`}
              className={`shrink-0 w-20 aspect-video overflow-hidden border rounded-sm bg-black ${i === index ? "border-blue-500 ring-1 ring-blue-500" : "border-zinc-800 opacity-60 hover:opacity-100"}`}
            >
              <AuthImg path={it.path} className="w-full h-full object-contain" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Row({ label, value, tone }) {
  const cls = tone === "pos" ? "text-emerald-400" : tone === "neg" ? "text-rose-400" : "text-zinc-200";
  return (
    <div className="flex justify-between">
      <span className="text-zinc-500">{label}</span>
      <span className={`font-mono ${cls}`}>{value}</span>
    </div>
  );
}

function fmtCompact(n, prefix = "") {
  if (n == null || !isFinite(n)) return "—";
  const abs = Math.abs(n);
  if (abs >= 1e12) return `${prefix}${(n / 1e12).toFixed(2)}T`;
  if (abs >= 1e9) return `${prefix}${(n / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${prefix}${(n / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${prefix}${(n / 1e3).toFixed(1)}K`;
  return `${prefix}${n}`;
}

function EditField({ label, value, onChange }) {
  return (
    <div className="flex items-center gap-2">
      <Label className="text-xs uppercase tracking-wider text-zinc-500 w-16">{label}</Label>
      <Input type="number" step="0.01" value={value} onChange={(e) => onChange(e.target.value)} data-testid={`edit-${label.toLowerCase()}-input`} className="bg-zinc-950 border-zinc-800 rounded-sm h-8 font-mono flex-1" />
    </div>
  );
}
